import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { 
  AppointmentWorkflow, 
  AppointmentStatus, 
  VitalSigns, 
  SOAPNotes, 
  Prescription, 
  LabOrder,
  WorkflowStep,
  WORKFLOW_STEPS 
} from '../types/workflow';
import { Appointment } from '../types';
import { 
  canStartWorkflow, 
  getWorkflowStatusFromAppointment, 
  shouldAutoAdvanceToWaiting,
  isAppointmentToday 
} from '../utils/appointmentWorkflowUtils';

interface WorkflowState {
  currentWorkflow: AppointmentWorkflow | null;
  workflows: AppointmentWorkflow[];
  loading: boolean;
  error: string | null;
}

type WorkflowAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_CURRENT_WORKFLOW'; payload: AppointmentWorkflow | null }
  | { type: 'SET_WORKFLOWS'; payload: AppointmentWorkflow[] }
  | { type: 'UPDATE_WORKFLOW_STATUS'; payload: { id: string; status: AppointmentStatus } }
  | { type: 'ADD_VITAL_SIGNS'; payload: { appointmentId: string; vitals: VitalSigns } }
  | { type: 'ADD_SOAP_NOTES'; payload: { appointmentId: string; soapNotes: SOAPNotes } }
  | { type: 'ADD_PRESCRIPTION'; payload: { appointmentId: string; prescription: Prescription } }
  | { type: 'ADD_LAB_ORDER'; payload: { appointmentId: string; labOrder: LabOrder } }
  | { type: 'ADVANCE_WORKFLOW_STEP'; payload: { appointmentId: string } }
  | { type: 'COMPLETE_WORKFLOW_STEP'; payload: { appointmentId: string; stepId: number } };

const initialState: WorkflowState = {
  currentWorkflow: null,
  workflows: [],
  loading: false,
  error: null,
};

function workflowReducer(state: WorkflowState, action: WorkflowAction): WorkflowState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    
    case 'SET_CURRENT_WORKFLOW':
      return { ...state, currentWorkflow: action.payload };
    
    case 'SET_WORKFLOWS':
      return { ...state, workflows: action.payload };
    
    case 'UPDATE_WORKFLOW_STATUS':
      const updatedWorkflows = state.workflows.map(workflow =>
        workflow.id === action.payload.id
          ? { ...workflow, status: action.payload.status }
          : workflow
      );
      return {
        ...state,
        workflows: updatedWorkflows,
        currentWorkflow: state.currentWorkflow?.id === action.payload.id
          ? { ...state.currentWorkflow, status: action.payload.status }
          : state.currentWorkflow
      };
    
    case 'ADD_VITAL_SIGNS':
      return {
        ...state,
        workflows: state.workflows.map(workflow =>
          workflow.appointmentId === action.payload.appointmentId
            ? { ...workflow, vitalSigns: action.payload.vitals, vitalsRecordedAt: new Date().toISOString() }
            : workflow
        ),
        currentWorkflow: state.currentWorkflow?.appointmentId === action.payload.appointmentId
          ? { ...state.currentWorkflow, vitalSigns: action.payload.vitals, vitalsRecordedAt: new Date().toISOString() }
          : state.currentWorkflow
      };
    
    case 'ADD_SOAP_NOTES':
      return {
        ...state,
        workflows: state.workflows.map(workflow =>
          workflow.appointmentId === action.payload.appointmentId
            ? { ...workflow, soapNotes: action.payload.soapNotes }
            : workflow
        ),
        currentWorkflow: state.currentWorkflow?.appointmentId === action.payload.appointmentId
          ? { ...state.currentWorkflow, soapNotes: action.payload.soapNotes }
          : state.currentWorkflow
      };
    
    case 'ADD_PRESCRIPTION':
      return {
        ...state,
        workflows: state.workflows.map(workflow =>
          workflow.appointmentId === action.payload.appointmentId
            ? { 
                ...workflow, 
                prescriptions: [...workflow.prescriptions, action.payload.prescription]
              }
            : workflow
        ),
        currentWorkflow: state.currentWorkflow?.appointmentId === action.payload.appointmentId
          ? { 
              ...state.currentWorkflow, 
              prescriptions: [...state.currentWorkflow.prescriptions, action.payload.prescription]
            }
          : state.currentWorkflow
      };
    
    case 'ADD_LAB_ORDER':
      return {
        ...state,
        workflows: state.workflows.map(workflow =>
          workflow.appointmentId === action.payload.appointmentId
            ? { 
                ...workflow, 
                labOrders: [...workflow.labOrders, action.payload.labOrder]
              }
            : workflow
        ),
        currentWorkflow: state.currentWorkflow?.appointmentId === action.payload.appointmentId
          ? { 
              ...state.currentWorkflow, 
              labOrders: [...state.currentWorkflow.labOrders, action.payload.labOrder]
            }
          : state.currentWorkflow
      };
    
    case 'ADVANCE_WORKFLOW_STEP':
      return {
        ...state,
        workflows: state.workflows.map(workflow =>
          workflow.appointmentId === action.payload.appointmentId
            ? { ...workflow, currentStep: Math.min(workflow.currentStep + 1, workflow.totalSteps) }
            : workflow
        ),
        currentWorkflow: state.currentWorkflow?.appointmentId === action.payload.appointmentId
          ? { ...state.currentWorkflow, currentStep: Math.min(state.currentWorkflow.currentStep + 1, state.currentWorkflow.totalSteps) }
          : state.currentWorkflow
      };
    
    default:
      return state;
  }
}

interface WorkflowContextType {
  state: WorkflowState;
  startWorkflow: (appointmentId: string, patientId: string, doctorId?: string) => void;
  startWorkflowFromAppointment: (appointment: Appointment) => { success: boolean; message?: string };
  updateWorkflowStatus: (id: string, status: AppointmentStatus) => void;
  addVitalSigns: (appointmentId: string, vitals: VitalSigns) => void;
  addSOAPNotes: (appointmentId: string, soapNotes: SOAPNotes) => void;
  addPrescription: (appointmentId: string, prescription: Prescription) => void;
  addLabOrder: (appointmentId: string, labOrder: LabOrder) => void;
  advanceWorkflowStep: (appointmentId: string) => void;
  completeWorkflowStep: (appointmentId: string, stepId: number) => void;
  getWorkflowSteps: (workflow: AppointmentWorkflow) => WorkflowStep[];
  getCurrentStep: (workflow: AppointmentWorkflow) => WorkflowStep | null;
  canCheckIn: (appointment: Appointment) => { canCheckIn: boolean; reason?: string };
  setCurrentWorkflow: (workflow: AppointmentWorkflow) => void;
}

const WorkflowContext = createContext<WorkflowContextType | undefined>(undefined);

export const useWorkflow = () => {
  const context = useContext(WorkflowContext);
  if (context === undefined) {
    throw new Error('useWorkflow must be used within a WorkflowProvider');
  }
  return context;
};

interface WorkflowProviderProps {
  children: ReactNode;
}

export const WorkflowProvider: React.FC<WorkflowProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(workflowReducer, initialState);

  const startWorkflow = (appointmentId: string, patientId: string, doctorId?: string) => {
    const workflow: AppointmentWorkflow = {
      id: `wf-${appointmentId}`,
      appointmentId,
      patientId,
      doctorId,
      status: 'CHECKED_IN',
      currentStep: 1,
      totalSteps: 7,
      prescriptions: [],
      labOrders: [],
      checkedInAt: new Date().toISOString(),
    };
    
    dispatch({ type: 'SET_CURRENT_WORKFLOW', payload: workflow });
    dispatch({ type: 'SET_WORKFLOWS', payload: [...state.workflows, workflow] });
  };

  const startWorkflowFromAppointment = (appointment: Appointment): { success: boolean; message?: string } => {
    // Check if workflow already exists
    const existingWorkflow = state.workflows.find(w => w.appointmentId === appointment.id);
    if (existingWorkflow) {
      dispatch({ type: 'SET_CURRENT_WORKFLOW', payload: existingWorkflow });
      return { success: true, message: 'Continuing existing workflow' };
    }

    // Validate if workflow can be started
    const validation = canStartWorkflow(appointment);
    if (!validation.canStart) {
      return { success: false, message: validation.reason };
    }

    // Determine initial status and step based on appointment
    const initialStatus = getWorkflowStatusFromAppointment(appointment);
    const autoAdvanceToWaiting = shouldAutoAdvanceToWaiting(appointment);

    const workflow: AppointmentWorkflow = {
      id: `wf-${appointment.id}`,
      appointmentId: appointment.id,
      patientId: appointment.patientId,
      doctorId: appointment.doctorId,
      status: autoAdvanceToWaiting ? 'WAITING' : initialStatus,
      currentStep: autoAdvanceToWaiting ? 2 : 1, // Auto-advance to waiting if same day
      totalSteps: 7,
      prescriptions: [],
      labOrders: [],
      checkedInAt: autoAdvanceToWaiting ? new Date().toISOString() : undefined,
    };
    
    dispatch({ type: 'SET_CURRENT_WORKFLOW', payload: workflow });
    dispatch({ type: 'SET_WORKFLOWS', payload: [...state.workflows, workflow] });
    
    return { 
      success: true, 
      message: autoAdvanceToWaiting 
        ? 'Patient automatically checked in and moved to waiting area' 
        : 'Workflow created successfully'
    };
  };

  const canCheckIn = (appointment: Appointment): { canCheckIn: boolean; reason?: string } => {
    const validation = canStartWorkflow(appointment);
    return {
      canCheckIn: validation.canStart,
      reason: validation.reason
    };
  };

  const setCurrentWorkflow = (workflow: AppointmentWorkflow) => {
    dispatch({ type: 'SET_CURRENT_WORKFLOW', payload: workflow });
  };

  const updateWorkflowStatus = (id: string, status: AppointmentStatus) => {
    dispatch({ type: 'UPDATE_WORKFLOW_STATUS', payload: { id, status } });
  };

  const addVitalSigns = (appointmentId: string, vitals: VitalSigns) => {
    dispatch({ type: 'ADD_VITAL_SIGNS', payload: { appointmentId, vitals } });
  };

  const addSOAPNotes = (appointmentId: string, soapNotes: SOAPNotes) => {
    dispatch({ type: 'ADD_SOAP_NOTES', payload: { appointmentId, soapNotes } });
  };

  const addPrescription = (appointmentId: string, prescription: Prescription) => {
    dispatch({ type: 'ADD_PRESCRIPTION', payload: { appointmentId, prescription } });
  };

  const addLabOrder = (appointmentId: string, labOrder: LabOrder) => {
    dispatch({ type: 'ADD_LAB_ORDER', payload: { appointmentId, labOrder } });
  };

  const advanceWorkflowStep = (appointmentId: string) => {
    // Check if current workflow requires verification before advancing
    const currentWorkflow = state.workflows.find(w => w.appointmentId === appointmentId);
    if (currentWorkflow && requiresVerification(currentWorkflow)) {
      console.warn('Cannot advance workflow: prescriptions/lab orders require verification');
      return;
    }
    dispatch({ type: 'ADVANCE_WORKFLOW_STEP', payload: { appointmentId } });
  };

  const requiresVerification = (workflow: AppointmentWorkflow): boolean => {
    // Check if any prescriptions need verification
    const unverifiedPrescriptions = workflow.prescriptions?.some(p => 
      !p.verificationStatus || p.verificationStatus === 'PENDING'
    );
    
    // Check if any lab orders need verification
    const unverifiedLabOrders = workflow.labOrders?.some(l => 
      !l.verificationStatus || l.verificationStatus === 'PENDING'
    );
    
    return Boolean(unverifiedPrescriptions || unverifiedLabOrders);
  };

  const completeWorkflowStep = (appointmentId: string, stepId: number) => {
    dispatch({ type: 'COMPLETE_WORKFLOW_STEP', payload: { appointmentId, stepId } });
  };

  const getWorkflowSteps = (workflow: AppointmentWorkflow): WorkflowStep[] => {
    return Object.values(WORKFLOW_STEPS).map(step => ({
      ...step,
      status: step.id < workflow.currentStep ? 'COMPLETED' : 
              step.id === workflow.currentStep ? 'IN_PROGRESS' : 'PENDING'
    }));
  };

  const getCurrentStep = (workflow: AppointmentWorkflow): WorkflowStep | null => {
    const stepTemplate = WORKFLOW_STEPS[workflow.currentStep];
    if (!stepTemplate) return null;
    
    return {
      ...stepTemplate,
      status: 'IN_PROGRESS'
    };
  };

  const value: WorkflowContextType = {
    state,
    startWorkflow,
    startWorkflowFromAppointment,
    updateWorkflowStatus,
    addVitalSigns,
    addSOAPNotes,
    addPrescription,
    addLabOrder,
    advanceWorkflowStep,
    completeWorkflowStep,
    getWorkflowSteps,
    getCurrentStep,
    canCheckIn,
    setCurrentWorkflow,
  };

  return (
    <WorkflowContext.Provider value={value}>
      {children}
    </WorkflowContext.Provider>
  );
};